import React from 'react';
import {
  Card,
  Text,
  Button,
  makeStyles,
  tokens,
  Title1,
  Caption1,
  Spinner,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ShieldCheckmarkRegular,
  DesktopRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/hooks/useAuth';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: '480px',
    width: '100%',
    padding: tokens.spacingVerticalXXL,
    margin: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  logo: {
    fontSize: '64px',
    color: tokens.colorBrandForeground1,
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalXL,
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  featureIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  loginButton: {
    width: '100%',
    height: '48px',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const { login, isLoading, error } = useAuth();

  const handleLogin = async () => {
    try {
      await login();
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <DesktopRegular className={styles.logo} />
          <Title1>AVD Self-Service Portal</Title1>
          <Caption1>Manage your Azure Virtual Desktop sessions</Caption1>
        </div>

        <div className={styles.features}>
          <div className={styles.feature}>
            <PersonRegular className={styles.featureIcon} />
            <div>
              <Text weight="semibold">Single Sign-On</Text>
              <br />
              <Caption1>Sign in with your corporate account</Caption1>
            </div>
          </div>

          <div className={styles.feature}>
            <ShieldCheckmarkRegular className={styles.featureIcon} />
            <div>
              <Text weight="semibold">Secure Access</Text>
              <br />
              <Caption1>Enterprise-grade security with Azure AD</Caption1>
            </div>
          </div>

          <div className={styles.feature}>
            <DesktopRegular className={styles.featureIcon} />
            <div>
              <Text weight="semibold">Session Management</Text>
              <br />
              <Caption1>View and manage your AVD sessions</Caption1>
            </div>
          </div>
        </div>

        <Button
          appearance="primary"
          className={styles.loginButton}
          onClick={handleLogin}
          disabled={isLoading}
          icon={isLoading ? <Spinner size="tiny" /> : <PersonRegular />}
        >
          {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
        </Button>

        {error && (
          <div className={styles.error}>
            <ErrorCircleRegular className={styles.errorIcon} />
            <Caption1>
              Failed to sign in. Please try again or contact support if the issue persists.
            </Caption1>
          </div>
        )}
      </Card>
    </div>
  );
};